import { doc, collection } from 'firebase/firestore'
import { observable, makeObservable, computed, action } from 'mobx'
import { getDocWithError } from '../firestore-mobx/fetch'
import type { FirebaseRepository } from '../models/FirebaseRepository'
import { noTryAsync } from '../util'

// To not pollute the codebase with backend-related code, we will solve fetching with a single
// class that can be reused to fetch different configs - we only use it in settings to display to
// admins.

export type ConfigRecord = Record<
  string,
  string | number | boolean | string[] | object
>
export type ConfigEntry = [
  string,
  string | number | boolean | string[] | object,
]

type BackendConfigModelStatus = 'idle' | 'loading' | 'loaded' | 'error'

export class BackendConfigModel {
  @observable
  status: BackendConfigModelStatus = 'idle'

  @observable
  errorMessage: string | null = null

  @observable
  private _data: ConfigRecord = {}

  repository: FirebaseRepository
  docName: string

  constructor(repository: FirebaseRepository, docName: string) {
    this.repository = repository
    this.docName = docName

    makeObservable(this)
  }

  @computed
  get isAdmin(): boolean {
    return this.repository.breakoutUser?.isAdmin ?? false
  }

  @computed
  get isLoaded(): boolean {
    return this.status === 'loaded'
  }

  @computed
  get isLoading(): boolean {
    return this.status === 'loading'
  }

  @computed
  get isError(): boolean {
    return this.status === 'error'
  }

  async fetchIfNotFetched(): Promise<void> {
    if (!this.isAdmin) {
      this.setErrorStatus(`Unauthorized access to ${this.docName} document`)
      return
    }

    if (this.status !== 'idle') return

    this.changeStatus('loading')

    const [, error] = await noTryAsync(async () => {
      const docRef = doc(
        collection(this.repository.firestore, 'settings'),
        this.docName
      )

      const fetched = await getDocWithError(docRef, `Fetch${this.docName}Error`)

      const data = fetched.data()
      if (!data) {
        throw new Error(`No data found in ${this.docName} document`)
      }

      this.changeStatus('loaded', data as ConfigRecord)
    })

    if (error) {
      this.changeStatus('error')
      this.setErrorStatus(error.message)
    }
  }

  @action
  changeStatus(newStatus: BackendConfigModelStatus, data?: ConfigRecord) {
    this.status = newStatus
    if (data) {
      this._data = data
    }
  }

  @action
  setErrorStatus(errorMessage: string | null) {
    this.status = 'error'
    this.errorMessage = errorMessage
    this._data = {}
  }

  @computed
  get data(): ConfigRecord {
    return this._data
  }

  @computed
  get entries(): ConfigEntry[] {
    return Object.entries(this.data).sort((a, b) => a[0].localeCompare(b[0]))
  }
}
